import { Component, type ErrorInfo, type ReactNode } from 'react';

interface State {
  hasError: boolean;
}

/**
 * Last line of defence. A render crash shows a branded recovery page rather than a blank
 * screen; the detail goes to the console, never to the visitor.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('Unhandled UI error', error, info.componentStack);
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'grid',
          placeItems: 'center',
          background: '#000',
          color: '#fff',
          padding: 24,
          fontFamily: "'Manrope', sans-serif",
          textAlign: 'center',
        }}
      >
        <div style={{ maxWidth: 480 }}>
          <p
            style={{
              fontSize: 11,
              letterSpacing: '0.2em',
              color: '#D2AA77',
              textTransform: 'uppercase',
              margin: '0 0 16px',
            }}
          >
            Something went wrong
          </p>
          <h1
            style={{
              fontFamily: "'Spectral', serif",
              fontSize: 30,
              margin: '0 0 14px',
              fontWeight: 500,
            }}
          >
            We hit an unexpected problem
          </h1>
          <p style={{ color: '#B0A483', fontSize: 14, lineHeight: 1.7, margin: '0 0 28px' }}>
            Reloading usually fixes it. If it keeps happening, please call the venue and we will take
            your booking directly.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              background: 'linear-gradient(135deg,rgba(210,170,119,0.95),rgba(168,110,52,0.85))',
              color: '#1a1207',
              border: '1px solid rgba(210,170,119,0.65)',
              padding: '15px 26px',
              fontSize: 12,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            Reload the page
          </button>
        </div>
      </div>
    );
  }
}
